import React, { useEffect, useState } from "react";
import api from "../../config/axios";
import Header from "../common/Header";
import "./ReservedTableList.css";
import { useNavigate } from "react-router-dom";
import AddItemsModal from "../order/AddItemsModal";

interface Table {
  _id: string;
  name: string;
  seats: number;
}

interface Reservation {
  _id: string;
  table: Table;
  customerName: string;
  customerPhone: string;
  peopleCount: number;
  time: string;
  note?: string;
  status: "reserved" | "in-use"| "cancelled" | "completed" | "expired";
  reservedItems?: any[];
}

interface ReservedTableListProps {
  user: { username: string; role: string };
  onLogout: () => void;
}

const ReservedTableList: React.FC<ReservedTableListProps> = ({ user, onLogout }) => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterDate, setFilterDate] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [addItemsOrderId, setAddItemsOrderId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Tìm đơn chưa thanh toán của bàn rồi mở modal thêm món.
  const openAddItems = async (tableId?: string) => {
    if (!tableId) return;
    try {
      const res = await api.get('/api/orders');
      const order = res.data.data.find((o: any) =>
        (o.status === 'pending' || o.status === 'processing') &&
        String(o.tableId?._id || o.tableId) === String(tableId)
      );
      if (!order) { alert('Bàn này chưa có đơn hàng chưa thanh toán.'); return; }
      setAddItemsOrderId(order._id);
    } catch {
      alert('Không tải được đơn hàng của bàn.');
    }
  };

  useEffect(() => {
    api.get("/api/tables/reservations")
      .then(res => setReservations(res.data))
      .catch(err => setError("Không thể tải danh sách bàn đã đặt"))
      .finally(() => setLoading(false));
  }, []);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Trống';
      case 'reserved': return 'Đã đặt';
      case 'in-use': return 'Đang sử dụng';
      case 'completed': return 'Hoàn thành';
      case 'expired': return 'Quá hạn';
      case 'cancelled': return 'Đã hủy';
      default: return status;
    }
  };

  const handleUpdateStatus = async (id: string, status: Reservation["status"], reservation?: any) => {
    try {
      await api.patch(`/api/tables/reservations/${id}`, { status });
      setReservations(rs => rs.map(r => r._id === id ? { ...r, status } : r));
      if (status === "in-use" && reservation) {
        const items = reservation.reservedItems || [];
        navigate(`/orders/new`, { state: { reservationId: id, tableId: reservation.table?._id, items } });
      }
    } catch (err) {
      alert("Không thể cập nhật trạng thái!");
    }
  };

  const filteredReservations = reservations.filter(rsv => {
    const rsvDate = new Date(rsv.time);
    const filter = new Date(filterDate);
    return rsvDate.getFullYear() === filter.getFullYear() &&
      rsvDate.getMonth() === filter.getMonth() &&
      rsvDate.getDate() === filter.getDate();
  });

  const handleRowClick = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowModal(true);
  };

  return (
    <div className="reserved-table-list-container">
      <Header user={user} onLogout={onLogout} />
      <div className="reserved-table-content-wrapper">
        <div className="reserved-table-header">
          <button className="back-btn" onClick={() => navigate(-1)} title="Quay lại">←</button>
          <h2 className="reserved-table-title">Quản lý bàn đã đặt</h2>
        </div>
        <div className="reservation-date-filter">
          <label htmlFor="filter-date">Lọc theo ngày:</label>
          <input
            id="filter-date"
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
          />
        </div>
        <div className="reserved-table-content">
        {loading && <p>Đang tải...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        <div className="reserved-table-scroll">
          <table className="reserved-table">
            <thead>
              <tr>
                <th>Bàn</th>
                <th>Khách hàng</th>
                <th>Số điện thoại</th>
                <th>Số người</th>
                <th>Thời gian</th>
                <th>Ghi chú</th>
                <th>Trạng thái</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {filteredReservations.map(rsv => {
                const isExpired = rsv.status === "reserved" && new Date(rsv.time) < new Date();
                return (
                  <tr key={rsv._id} className={isExpired ? "expired" : rsv.status} style={{ cursor: "pointer" }} onClick={() => handleRowClick(rsv)}>
                    <td>{rsv.table?.name}</td>
                    <td>{rsv.customerName}</td>
                    <td>{rsv.customerPhone}</td>
                    <td>{rsv.peopleCount}</td>
                    <td>{new Date(rsv.time).toLocaleString()}</td>
                    <td>{rsv.note}</td>
                    <td>{getStatusText(rsv.status)}</td>
                    <td>
                      {rsv.status === "reserved" && !isExpired && (
                        <>
                          <button className="reservation-action-btn arrived" onClick={e => { e.stopPropagation(); handleUpdateStatus(rsv._id, "in-use", rsv); }}>Khách đã đến</button>{' '}
                          <button className="reservation-action-btn cancel" onClick={e => { e.stopPropagation(); handleUpdateStatus(rsv._id, "cancelled"); }}>Hủy</button>
                        </>
                      )}
                      {rsv.status === "in-use" && (
                        <>
                          <button className="reservation-action-btn arrived" onClick={e => { e.stopPropagation(); openAddItems(rsv.table?._id); }}>+ Thêm món</button>{' '}
                          <button className="reservation-action-btn complete" onClick={e => { e.stopPropagation(); handleUpdateStatus(rsv._id, "completed"); }}>Hoàn thành</button>
                        </>
                      )}
                      {isExpired && rsv.status === "reserved" && (
                        <button className="reservation-action-btn expired" onClick={e => { e.stopPropagation(); handleUpdateStatus(rsv._id, "expired"); }}>Đánh dấu quá hạn</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredReservations.length === 0 && !loading && <p>Không có bàn nào được đặt trong ngày này.</p>}
        </div>
      </div>
      {showModal && selectedReservation && (
        <div className="reservation-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="reservation-modal" onClick={e => e.stopPropagation()}>
            <h3>Chi tiết món đã đặt</h3>
            <p><b>Bàn:</b> {selectedReservation.table?.name}</p>
            <p><b>Khách:</b> {selectedReservation.customerName}</p>
            {selectedReservation.reservedItems && selectedReservation.reservedItems.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Ảnh</th>
                    <th>Tên món</th>
                    <th>Số lượng</th>
                    <th>Đơn giá</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedReservation.reservedItems.map((item: any, idx: number) => (
                    <tr key={idx}>
                      <td>
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.name} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <span style={{ color: '#aaa' }}>Không có</span>
                        )}
                      </td>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.price?.toLocaleString()}đ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>Chưa có món nào được đặt cho bàn này.</p>
            )}
            <button className="close-btn" onClick={() => setShowModal(false)}>Đóng</button>
          </div>
        </div>
      )}
      {addItemsOrderId && (
        <AddItemsModal
          orderId={addItemsOrderId}
          onClose={() => setAddItemsOrderId(null)}
          onAdded={() => { /* đã thêm; có thể refresh nếu cần */ }}
        />
      )}
    </div>
  );
};

export default ReservedTableList; 